import { html, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import componentStyles from './sidebar-nav.css.lit.ts';
import { OutlineElement } from '../../base/outline-element/outline-element.ts';

export default class SidebarNav extends OutlineElement {
  static styles = [super.styles, componentStyles];
  static get properties() {
    return {
      flourish: { type: Array },
      links: { type: Array },
      sidebarTitle: { type: String },
    };
  }

  isCustomElement(targetElement, currentElement) {
    // const isAttr = currentElement.getAttribute('bodytext')
    // Check for <h2> and bodytext="h2" attribute.
    // return currentElement.localName.includes(targetElement) || isAttr && isAttr.includes(targetElement)
    return currentElement.localName.includes(targetElement);
  }

  deepSearch(element, nodes) {
    Array.prototype.forEach.call(nodes, node => {
      if (this.isCustomElement(element, node)) {
        this.tocNodes.push(node);
      }
      // If the element has shadow DOM, dig deeper.
      if (node.shadowRoot) {
        this.deepSearch(element, node.shadowRoot.querySelectorAll('*'));
      }
    });
  }

  fetchTitle(node) {
    let title = '';
    if (node.textContent) {
      title = node.textContent;
    } else if (node.querySelector('slot')) {
      // fetch text content from any nested structure
      let extractedText = '';
      const slotStructure = node.querySelector('slot').assignedNodes();
      Array.prototype.forEach.call(slotStructure, el => {
        if (el.textContent.replaceAll(' ', '') !== '') {
          extractedText += el.textContent;
        }
      });
      title = extractedText;
    }
    return title;
  }

  buildToc(element) {
    this.deepSearch(
      element,
      document.querySelector('#main-document').querySelectorAll('*')
    );
    Array.prototype.forEach.call(this.tocNodes, (node, index) => {
      // Make the element focusable
      node.setAttribute('tabindex', '-1');

      // Fetch the title
      const title = this.fetchTitle(node);

      // Set the link with its target node
      const link = {
        text: title,
        url: `#${title.toLowerCase().replace(/\W/g, '-')}`,
        active: false,
        data: node,
      };

      // Highlight the link that corresponds with the current ToC item.
      const onIntersection = entries => {
        // linksArrayCopy is used for stencil to keep track of links array updates.
        if (!this.isMobile) {
          const linksArrayCopy = [...this.links];
          // eslint-disable-next-line no-restricted-syntax
          for (const entry of entries) {
            if (entry.intersectionRatio > 0) {
              window.history.replaceState(null, null, link.url);
              Array.prototype.forEach.call(linksArrayCopy, linkItem => {
                // eslint-disable-next-line no-param-reassign
                linkItem.active = false;
              });
              linksArrayCopy[index].active = true;
            }
            this.links = [...linksArrayCopy];
          }
        }
      };

      // Attach observer for each link
      const observer = new IntersectionObserver(onIntersection);
      observer.observe(node);

      // Save the link to list of links
      this.links.push(link);
    });
  }

  checkMobile() {
    this.update();
    this.isMobile = window.innerWidth < 1200;
  }

  jumpToHash() {
    if (window.location.hash !== '') {
      Array.prototype.forEach.call(this.links, link => {
        if (link.url === window.location.hash) {
          this.linkToElement(link);
        }
      });
    }
  }

  constructor() {
    super();
    this.links = [];
    this.tocNodes = [];
    // Let document's various web components finish loading first
    setTimeout(() => {
      this.buildToc('h2');
      this.checkMobile();
      this.jumpToHash();
      window.addEventListener('resize', event => this.checkMobile(event));
    }, 0);
  }

  linkToElement(elementLink) {
    // Set only the current link as active
    const linksArrayCopy = [...this.links];
    Array.prototype.forEach.call(linksArrayCopy, link => {
      // eslint-disable-next-line no-param-reassign
      link.active = link.url === elementLink.url;
    });
    // linksArrayCopy is used for stencil/lit to keep track of links array updates.
    this.links = [...linksArrayCopy];
    // Update browser address bar with the new hash link
    window.history.replaceState(null, null, elementLink.url);
    // Jump to element
    elementLink.data.scrollIntoView();
    elementLink.data.focus();
  }

  render() {
    const flourish = this.flourish
      ? html`
          <div class="sidebar-navigation__flourish">
            <div class="sidebar-navigation__flourish-return">
              <span>Return To</span>
            </div>
            <a href="${this.flourish.url}">
              <hf-icon
                class="sidebar-navigation__flourish-icon"
                name="chevron-left"
              ></hf-icon>
              <span>${this.flourish.text}</span>
            </a>
          </div>
        `
      : nothing;

    const list = html`<ul>
      ${this.links.map(link => {
        return html` <li class="sidebar-navigation--item">
          <button
            tabindex="0"
            role="link"
            class=${classMap({
              'active': !!link.active,
              'sidebar-navigation--link': true,
            })}
            @click=${() => this.linkToElement(link)}
          >
            ${link.text}
          </button>
        </li>`;
      })}
    </ul>`;

    return html`
      <nav
        role="navigation"
        class="${classMap({
          'flourish': !!this.flourish,
          'sidebar-navigation': true,
        })}"
      >
        <details class="sidebar-navigation--mobile">
          <summary>${this.sidebarTitle}</summary>
          ${flourish} ${list}
        </details>
        <div class="sidebar-navigation--desktop">${flourish} ${list}</div>
      </nav>
    `;
  }
}

customElements.define('hf-sidebar-nav', SidebarNav);
